import type { MouseEvent as ReactMouseEvent } from "react";
import { Link } from "react-router-dom";
import type { DeepLinkKind } from "../lib/deeplinks";

/**
 * Einheitliche Deep-Link-Komponente (Designsystem, BEN-92).
 *
 * - Externe Ziele (OpenProject / OpenSearch-Dashboards) oeffnen im NEUEN Tab
 *   (`target="_blank" rel="noopener noreferrer"`), klar beschriftet + Icon.
 * - Interne Cross-Links (Treiberbaum / Evidence / Cockpit) nutzen den
 *   React-Router `<Link>` (SPA-Navigation, kein neues Tab).
 * - Fehlt `href` (Ziel-ID nicht vorhanden), rendert die Komponente NICHTS —
 *   sauber ausgeblendet statt totem Link.
 */
export type DeepLinkProps = {
  href?: string | null;
  label: string;
  kind?: DeepLinkKind;
  /** erzwingt/verhindert neues Tab; Default: extern-artige kinds -> neues Tab */
  external?: boolean;
  title?: string;
  className?: string;
  onClick?: (e: ReactMouseEvent) => void;
};

const ICON: Record<DeepLinkKind, string> = {
  openproject: "↗", // ↗
  dashboard: "↗",
  cockpit: "→", // →
  extern: "↗",
  intern: "→",
};

function isExternalKind(kind: DeepLinkKind): boolean {
  return kind === "openproject" || kind === "dashboard" || kind === "extern";
}

export function DeepLink({
  href,
  label,
  kind = "extern",
  external,
  title,
  className,
  onClick,
}: DeepLinkProps) {
  if (!href) return null;
  const ext = external ?? isExternalKind(kind);
  const cls = `deep-link kind-${kind}${className ? " " + className : ""}`;
  const icon = ICON[kind] ?? "↗";
  if (ext) {
    return (
      <a
        className={cls}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title ?? label}
        onClick={onClick}
      >
        <span className="deep-link-label">{label}</span>
        <span className="deep-link-ic" aria-hidden>
          {icon}
        </span>
      </a>
    );
  }
  return (
    <Link className={cls} to={href} title={title ?? label} onClick={onClick}>
      <span className="deep-link-label">{label}</span>
      <span className="deep-link-ic" aria-hidden>
        {icon}
      </span>
    </Link>
  );
}

export type DeepLinkItem = (DeepLinkProps & { key?: string }) | null | undefined | false;

/**
 * Reihe aus Deep-Links. Eintraege ohne `href` (oder null/false) werden
 * ausgeblendet; ist am Ende nichts uebrig, rendert die Bar NICHTS.
 */
export function DeepLinkBar({
  links,
  label,
  className,
}: {
  links: DeepLinkItem[];
  label?: string;
  className?: string;
}) {
  const shown = (links || []).filter(
    (l): l is DeepLinkProps & { key?: string } => Boolean(l && (l as DeepLinkProps).href),
  );
  if (shown.length === 0) return null;
  return (
    <div className={`deep-link-bar${className ? " " + className : ""}`}>
      {label && <span className="deep-link-bar-k">{label}</span>}
      {shown.map((l, i) => (
        <DeepLink key={l.key ?? i} {...l} />
      ))}
    </div>
  );
}
